// The way in: the legacy Login page.
//
// The page is painted in palette.base.main edge to edge — the product blue for
// everybody, the branding comes only once somebody is in — with BaseNav across
// the top on a wide screen, the illustration on the left half and the form in
// a Paper of base.secondary with a 24px radius. The field and the button are
// both 62px tall with an 8px radius.
//
// The legacy form had one field and handed over to Keycloak. The portal signs
// in itself, so the same card asks for the password after the address — in
// the same field, with the same button — unless the company signs in through
// its own provider, in which case the browser goes there as before.

import { Fragment, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Grid,
  Link,
  Paper,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { lighten } from "polished";
import { api, ApiError, type Discovery } from "~/api";
import { useT } from "~/i18n";
import { setPrimary } from "~/theme";
import AppStoreIcon from "~/legacy/icons/AppStoreIcon";
import BaseLogoIcon from "~/legacy/icons/BaseLogoIcon";
import GooglePlayIcon from "~/legacy/icons/GooglePlayIcon";

const EMAIL = /^[a-zA-Z0-9.+_-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

/** The legacy BaseNav: the product and two links out, gone below 1000px. */
export function BaseNav() {
  const theme = useTheme();
  const below1000 = useMediaQuery("(max-width: 1000px)");
  const t = useT();
  const link = {
    mt: 2.3,
    fontSize: "16px",
    textAlign: "center",
    fontWeight: 700,
    lineHeight: "normal",
    cursor: "pointer",
    "&:hover": { color: theme.palette.base.main },
  } as const;
  return (
    <Grid
      container
      sx={{
        bgcolor: theme.palette.mode === "dark" ? lighten(0.05, theme.palette.background.paper) : "base.secondary",
        borderRadius: "20px",
        width: "100%",
        height: "97px",
        alignContent: "center",
        px: 3,
        display: below1000 ? "none" : "flex",
      }}
    >
      <Box sx={{ cursor: "pointer" }}>
        <BaseLogoIcon />
      </Box>
      <Link
        href="https://www.mobilboxmanager.info/"
        underline="none"
        color="inherit"
        sx={{ ml: 1.5, mt: 1.5, fontSize: "24px", fontWeight: 700, lineHeight: "normal", cursor: "pointer" }}
      >
        Mobilbox Manager
      </Link>
      <Link href="https://www.mobilboxmanager.info/kunden/" underline="none" color="inherit" sx={{ ml: 8, ...link }}>
        {t("references")}
      </Link>
      <Link href="https://www.mobilboxmanager.info/kontakt/" underline="none" color="inherit" sx={{ ml: 6, ...link }}>
        {t("contact")}
      </Link>
      <Box sx={{ flexGrow: 1 }} />
    </Grid>
  );
}

/** The page around a card: the blue ground, BaseNav, the illustration. */
export function SignInFrame({ children, apps = true }: { children: React.ReactNode; apps?: boolean }) {
  const theme = useTheme();
  const belowsm = useMediaQuery(theme.breakpoints.down("sm"));
  const belowmd = useMediaQuery(theme.breakpoints.down("md"));
  const belowlg = useMediaQuery(theme.breakpoints.down("lg"));
  const abovexl = useMediaQuery(theme.breakpoints.up("xl"));
  const below1000 = useMediaQuery("(max-width: 1000px)");
  const [height, setHeight] = useState(window.innerHeight);

  useEffect(() => {
    const s = document.body.style;
    s.background = theme.palette.base.main;
    s.backgroundImage = "none";
    const onResize = () => setHeight(window.innerHeight);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      s.background = "";
    };
  }, [theme.palette.base.main]);

  return (
    <Fragment>
      <Box sx={{ mt: below1000 ? 0 : 3, mx: 6 }}>
        <BaseNav />
      </Box>
      <Grid container>
        <Grid
          item
          xs={6}
          sx={{ display: belowmd ? "none" : "flex", justifyContent: "flex-end", alignContent: "center", pr: 8, mt: abovexl ? 15 : 0 }}
        >
          <img alt="login" src="/images/login.png" width="70%" style={{ aspectRatio: 1000 / 1296, objectFit: "scale-down" }} />
        </Grid>
        <Grid item xs={12} md={6}>
          <Box sx={{ height, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
            <Paper
              elevation={1}
              sx={{
                mt: belowlg && !belowsm ? 10 : 0,
                px: 4,
                backgroundImage: "none",
                bgcolor: theme.palette.mode === "dark" ? lighten(0.05, theme.palette.background.paper) : "base.secondary",
                borderRadius: 6,
                display: "inherit",
                flexDirection: "inherit",
                justifyContent: "inherit",
                alignItems: "inherit",
              }}
            >
              {children}
            </Paper>
            {apps ? (
              <Grid container columnGap={1.5} sx={{ justifyContent: "center", mt: -3.5, display: { xs: "none", md: "flex" } }}>
                <GooglePlayIcon />
                <Grid item sx={{ mt: 1.2 }}>
                  <AppStoreIcon />
                </Grid>
              </Grid>
            ) : null}
          </Box>
        </Grid>
      </Grid>
    </Fragment>
  );
}

/** The product mark and name at the top of the card. */
export function CardHead() {
  return (
    <Grid container sx={{ justifyContent: "center", alignContent: "center", pt: 4 }}>
      <BaseLogoIcon />
      <Typography sx={{ ml: 1.5, mt: 1.5, fontSize: "24px", fontWeight: 700, lineHeight: "normal" }}>Mobilbox Manager</Typography>
    </Grid>
  );
}

/** The legacy e-mail field: no outline, a white ground, the label inside. */
export function CardField({
  label,
  value,
  onChange,
  error,
  helperText,
  type,
  autoComplete,
  disabled,
  autoFocus,
  onEnter,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  error?: boolean;
  helperText?: string;
  type: string;
  autoComplete?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  onEnter?: () => void;
}) {
  const theme = useTheme();
  return (
    <TextField
      variant="outlined"
      label={label}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      size="small"
      onKeyDown={(e) => {
        if (e.key === "Enter") onEnter?.();
      }}
      error={error}
      helperText={helperText || " "}
      fullWidth
      required
      autoFocus={autoFocus}
      autoComplete={autoComplete}
      sx={{
        mt: 4,
        height: "62px",
        "& label": {
          mt: value.length > 0 ? 2 : 1,
          fontSize: "16px",
          fontWeight: 700,
        },
        "& label.Mui-focused": {
          mt: 2,
          color: "text.primary",
        },
        "& .MuiOutlinedInput-root": {
          pt: 2,
          pb: 0.5,
          borderRadius: "8px",
          bgcolor: theme.palette.mode === "dark" ? "#000" : "#fff",
        },
        "& .MuiOutlinedInput-notchedOutline": {
          border: "none",
        },
      }}
      disabled={disabled}
    />
  );
}

/** The legacy button: 62px, radius 8, the fixed product blue. */
export function CardButton({
  children,
  onClick,
  disabled,
  error,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  error?: boolean;
}) {
  return (
    <Button
      variant="contained"
      color="base"
      disableElevation
      fullWidth
      onClick={onClick}
      disabled={disabled}
      sx={{ mt: error ? 3.5 : 1.5, mb: error ? 2 : 4, height: "62px", borderRadius: "8px", textTransform: "none" }}
    >
      <Typography sx={{ fontSize: "16px", textAlign: "center", fontWeight: 700, lineHeight: "normal", color: "#fff" }}>
        {children}
      </Typography>
    </Button>
  );
}

export function SignInPage() {
  const t = useT();
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);

  const [email, setEmail] = useState(params.get("email") ?? "");
  const [found, setFound] = useState<Discovery | null>(null);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(params.get("fehler") ?? "");
  const [sent, setSent] = useState(false);

  useEffect(() => {
    document.title = t("vmm-login");
  }, [t]);

  async function discover() {
    if (!EMAIL.test(email)) {
      setError(t("invalid-email"));
      return;
    }
    setBusy(true);
    setError("");
    try {
      const d = await api<Discovery>(`/api/portal/auth/discover?email=${encodeURIComponent(email)}`);
      // Remembered for the first paint after signing in, as the legacy
      // application kept localStorage "primary".
      setPrimary(d.branding?.accentColor);
      setFound(d);
      if (d.method === "oidc" && d.provider) {
        window.location.href = d.provider.start;
        return;
      }
    } catch (err) {
      setError(err instanceof ApiError && err.status === 404 ? t("email-does-not-exist") : err instanceof ApiError ? err.detail : t("something-went-wrong"));
    }
    setBusy(false);
  }

  async function signIn() {
    setBusy(true);
    setError("");
    try {
      await api("/api/portal/session", { method: "POST", body: { email, password } });
      navigate("/", { replace: true });
      window.location.reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : t("sign-in-failed"));
      setBusy(false);
    }
  }

  async function forgot() {
    setBusy(true);
    try {
      await api("/api/portal/auth/password/forgot", { method: "POST", body: { email } });
    } finally {
      setSent(true);
      setBusy(false);
    }
  }

  const asking = found?.method === "password";

  return (
    <SignInFrame>
      <CardHead />
      {!asking ? (
        <Fragment>
          <CardField
            label="E-Mail"
            type="email"
            autoComplete="username"
            value={email}
            onChange={(v) => {
              if (v.length === 0) setError("");
              setEmail(v);
            }}
            error={!!error}
            helperText={error}
            disabled={busy}
            onEnter={discover}
          />
          <CardButton onClick={discover} disabled={busy} error={!!error}>
            {t("login")}
          </CardButton>
        </Fragment>
      ) : (
        <Fragment>
          <Typography sx={{ mt: 2, fontSize: 14, color: "text.secondary", alignSelf: "stretch", textAlign: "center" }}>
            {email}
          </Typography>
          <CardField
            label={t("password")}
            type="password"
            autoComplete="current-password"
            autoFocus
            value={password}
            onChange={setPassword}
            error={!!error}
            helperText={error || (sent ? t("forgot-sent") : "")}
            disabled={busy}
            onEnter={signIn}
          />
          <CardButton onClick={signIn} disabled={busy || !password} error={!!error || sent}>
            {t("login")}
          </CardButton>
          {found?.canResetPassword && !sent ? (
            <Link component="button" type="button" onClick={forgot} disabled={busy} underline="none" sx={{ mt: -2.5, mb: 3, fontSize: 14, fontWeight: 700, color: "base.main" }}>
              {t("forgot-password")}
            </Link>
          ) : null}
        </Fragment>
      )}
    </SignInFrame>
  );
}
